import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'
import { technologiesApi } from './api'
import { handleApiError } from '@/lib/api/axios'
import { useAuthStore } from '@/app/store/auth'
import type { Technology } from '@/types/entities'
import type { ListParams } from '@/lib/api/types'

// Query keys
export const technologyKeys = {
    all: ['technologies'] as const,
    lists: () => [...technologyKeys.all, 'list'] as const,
    list: (params: ListParams) => [...technologyKeys.lists(), params] as const,
    details: () => [...technologyKeys.all, 'detail'] as const,
    detail: (id: number) => [...technologyKeys.details(), id] as const,
}

// Technology hooks
export function useTechnologies(params?: ListParams) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery({
        queryKey: technologyKeys.list(params || {}),
        queryFn: () => technologiesApi.list(params),
        enabled: isAuthenticated && !mfaRequired,
        placeholderData: keepPreviousData,
    })
}

export function useTechnology(id: number) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery({
        queryKey: technologyKeys.detail(id),
        queryFn: () => technologiesApi.get(id),
        select: (response) => response.data,
        enabled: !!id && isAuthenticated && !mfaRequired,
    })
}

export function useCreateTechnology() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: (data: Omit<Technology, 'id' | 'createdAt' | 'updatedAt'>) =>
            technologiesApi.create(data),
        onSuccess: (response) => {
            queryClient.invalidateQueries({ queryKey: technologyKeys.lists() })
            toast({
                title: 'Technology Created',
                description: `${response.data.manufacturer} ${response.data.model} has been created successfully.`,
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: handleApiError(error),
                variant: 'destructive',
            })
        },
    })
}

export function useUpdateTechnology() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: Partial<Omit<Technology, 'id' | 'createdAt' | 'updatedAt'>> }) =>
            technologiesApi.update(id, data),
        onSuccess: (response, variables) => {
            queryClient.invalidateQueries({ queryKey: technologyKeys.lists() })
            queryClient.invalidateQueries({ queryKey: technologyKeys.detail(variables.id) })
            toast({
                title: 'Technology Updated',
                description: `${response.data.manufacturer} ${response.data.model} has been updated successfully.`,
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: handleApiError(error),
                variant: 'destructive',
            })
        },
    })
}

export function useDeleteTechnology() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: (id: number) => technologiesApi.delete(id),
        onSuccess: (_, id) => {
            queryClient.invalidateQueries({ queryKey: technologyKeys.lists() })
            queryClient.removeQueries({ queryKey: technologyKeys.detail(id) })
            toast({
                title: 'Technology Deleted',
                description: 'Technology has been deleted successfully.',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: handleApiError(error),
                variant: 'destructive',
            })
        },
    })
}
